import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Save, 
  User, 
  Building, 
  FileText, 
  Shield, 
  MessageSquare, 
  Settings,
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  Plus,
  X
} from 'lucide-react';
import { useToast } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import API_CONFIG from '../../config/api';

const EditTeam = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { success, error: showError } = useToast();

  // Tab management
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(true);
  const [skillInput, setSkillInput] = useState('');
  const [certInput, setCertInput] = useState('');
  const [projectRoleInput, setProjectRoleInput] = useState('');

  // Form state - same structure as CreateTeam
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    department: '',
    position: '',
    role: 'Team Member',
    status: 'active',
    joinDate: new Date().toISOString().split('T')[0],
    skills: [],
    experience: '',
    education: '',
    certifications: [],
    assignedProjects: [],
    projectRoles: [],
    permissions: {
      canCreateProjects: false,
      canEditProjects: false,
      canDeleteProjects: false,
      canAssignTasks: false,
      canViewReports: false,
      canManageTeam: false,
      canAccessFinance: false
    },
    notificationPreferences: {
      emailNotifications: true,
      projectUpdates: true,
      taskAssignments: true,
      deadlineReminders: true,
      teamMessages: true
    },
    bio: '',
    avatar: null,
    emergencyContact: {
      name: '',
      phone: '',
      relationship: ''
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);

  // Tab configuration - matching CreateTeam
  const tabs = [
    {
      id: 'basic',
      label: 'Basic Info',
      icon: <User size={18} />
    },
    {
      id: 'professional',
      label: 'Professional',
      icon: <Building size={18} />
    },
    {
      id: 'projects',
      label: 'Projects',
      icon: <FileText size={18} />
    },
    {
      id: 'permissions',
      label: 'Permissions',
      icon: <Shield size={18} />
    },
    {
      id: 'collaboration',
      label: 'Collaboration',
      icon: <MessageSquare size={18} />
    },
    {
      id: 'additional',
      label: 'Additional',
      icon: <Settings size={18} />
    }
  ];

  // Load projects
  const loadProjects = async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.PROJECTS, {
        headers: API_CONFIG.HEADERS
      });
      if (response.ok) {
        const data = await response.json();
        const projectsArray =
          Array.isArray(data.data) ? data.data :
          Array.isArray(data.data?.projects) ? data.data.projects :
          Array.isArray(data.projects) ? data.projects :
          [];
        setProjects(projectsArray);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Load team member data on mount
  useEffect(() => {
    const loadTeamMember = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_CONFIG.ENDPOINTS.TEAM_MEMBERS}/${id}`, {
          headers: API_CONFIG.HEADERS
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to load team member');
        }

        const data = await response.json();
        const member = data.data || {};

        setFormData({
          name: member.name || '',
          email: member.email || '',
          phone: member.phone || '',
          location: member.location || '',
          department: member.department || '',
          position: member.position || '',
          role: member.role || 'Team Member',
          status: member.status || 'active',
          joinDate: member.joinDate ? new Date(member.joinDate).toISOString().split('T')[0] : '',
          skills: member.skills || [],
          experience: member.experience || '',
          education: member.education || '',
          certifications: member.certifications || [],
          assignedProjects: (member.assignedProjects || []).map((project) => ({
            project: project.project || project._id || project.id,
            projectName: project.projectName || project.name || 'Project'
          })),
          projectRoles: member.projectRoles || [],
          permissions: {
            canCreateProjects: member.permissions?.canCreateProjects || false,
            canEditProjects: member.permissions?.canEditProjects || false,
            canDeleteProjects: member.permissions?.canDeleteProjects || false,
            canAssignTasks: member.permissions?.canAssignTasks || false,
            canViewReports: member.permissions?.canViewReports || false,
            canManageTeam: member.permissions?.canManageTeam || false,
            canAccessFinance: member.permissions?.canAccessFinance || false
          },
          notificationPreferences: {
            emailNotifications: member.notificationPreferences?.emailNotifications ?? true,
            projectUpdates: member.notificationPreferences?.projectUpdates ?? true,
            taskAssignments: member.notificationPreferences?.taskAssignments ?? true,
            deadlineReminders: member.notificationPreferences?.deadlineReminders ?? true,
            teamMessages: member.notificationPreferences?.teamMessages ?? true
          },
          bio: member.bio || '',
          avatar: member.avatar || null,
          emergencyContact: {
            name: member.emergencyContact?.name || '',
            phone: member.emergencyContact?.phone || '',
            relationship: member.emergencyContact?.relationship || ''
          }
        });

        await loadProjects();
      } catch (err) {
        showError(err.message || 'Failed to load team member data');
      } finally {
        setLoading(false);
      }
    };

    loadTeamMember();
  }, [id]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parentField, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [field]: value
      }
    }));
  };

  const handleArrayAdd = (field, value) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
    }
  };

  const handleArrayRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        assignedProjects: formData.assignedProjects.map((project) => ({
          project: project.project,
          projectName: project.projectName
        }))
      };

      const response = await fetch(`${API_CONFIG.ENDPOINTS.TEAM_MEMBERS}/${id}`, {
        method: 'PUT',
        headers: API_CONFIG.HEADERS,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update team member');
      }

      success('Team member updated successfully');
      navigate('/projects/team-collaboration');
    } catch (err) {
      showError(err.message || 'Failed to update team member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/projects/team-collaboration');
  };

  if (loading) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader 
          title="Edit Team Member"
          description="Loading team member details..."
        />
        <div className="page-container">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Edit Team Member"
        description="Update team member profile, role, and permissions"
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Main Content */}
        <div className="project-create-container" style={{ width: '100%', maxWidth: '100%' }}>
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            {/* Tab Navigation */}
            <div className="project-tabs">
              <div className="project-tabs-nav">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <div className="project-tab-icon">
                      {tab.icon}
                    </div>
                    <div className="project-tab-content">
                      <div className="project-tab-label">{tab.label}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="project-form-card">
              {/* Basic Information Tab */}
              {activeTab === 'basic' && (
                <div className="team-form-section">
                  <h4 className="mb-4">Basic Information</h4>
                  
                  <div className="form-grid-2">
                    <div className="form-group">
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Email Address *</label>
                      <input
                        type="email"
                        className="form-control"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Phone Number</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Location</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.location}
                        onChange={(e) => handleInputChange('location', e.target.value)}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Department *</label>
                      <select
                        className="form-select"
                        value={formData.department}
                        onChange={(e) => handleInputChange('department', e.target.value)}
                        required
                      >
                        <option value="">Select Department</option>
                        <option value="Engineering">Engineering</option>
                        <option value="Design">Design</option>
                        <option value="Business">Business</option>
                        <option value="Quality Assurance">Quality Assurance</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Sales">Sales</option>
                        <option value="HR">Human Resources</option>
                        <option value="Finance">Finance</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Position</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.position}
                        onChange={(e) => handleInputChange('position', e.target.value)}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Role *</label>
                      <select
                        className="form-select"
                        value={formData.role}
                        onChange={(e) => handleInputChange('role', e.target.value)}
                        required
                      >
                        <option value="Team Member">Team Member</option>
                        <option value="Project Manager">Project Manager</option>
                        <option value="Senior Developer">Senior Developer</option>
                        <option value="UX Designer">UX Designer</option>
                        <option value="DevOps Engineer">DevOps Engineer</option>
                        <option value="Business Analyst">Business Analyst</option>
                        <option value="QA Engineer">QA Engineer</option>
                        <option value="Team Lead">Team Lead</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={formData.status}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Join Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.joinDate}
                        onChange={(e) => handleInputChange('joinDate', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Professional Details Tab */}
              {activeTab === 'professional' && (
                <div className="team-form-section">
                  <h4 className="mb-4">Professional Details</h4>
                  
                  <div className="form-group mb-4">
                    <label className="form-label">Skills</label>
                    <div className="array-input-group">
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          value={skillInput}
                          onChange={(e) => setSkillInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleArrayAdd('skills', skillInput);
                              setSkillInput('');
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => {
                            handleArrayAdd('skills', skillInput);
                            setSkillInput('');
                          }}
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                      <div className="array-items">
                        {formData.skills.map((skill, index) => (
                          <div key={index} className="array-item">
                            <span>{skill}</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleArrayRemove('skills', index)}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="form-group mb-4">
                    <label className="form-label">Years of Experience</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.experience}
                      onChange={(e) => handleInputChange('experience', e.target.value)}
                      min="0"
                      max="50"
                    />
                  </div>
                  
                  <div className="form-group mb-4">
                    <label className="form-label">Education</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.education}
                      onChange={(e) => handleInputChange('education', e.target.value)}
                      placeholder="Educational background..."
                    />
                  </div>
                  
                  <div className="form-group mb-4">
                    <label className="form-label">Certifications</label>
                    <div className="array-input-group">
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Add a certification"
                          value={certInput}
                          onChange={(e) => setCertInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleArrayAdd('certifications', certInput);
                              setCertInput('');
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => {
                            handleArrayAdd('certifications', certInput);
                            setCertInput('');
                          }}
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                      <div className="array-items">
                        {formData.certifications.map((cert, index) => (
                          <div key={index} className="array-item">
                            <span>{cert}</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleArrayRemove('certifications', index)}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Project Assignment Tab */}
              {activeTab === 'projects' && (
                <div className="team-form-section">
                  <h4 className="mb-4">Project Assignment</h4>
                  
                  <div className="form-group mb-4">
                    <label className="form-label">Assigned Projects</label>
                    <div className="array-input-group">
                      <div className="input-group">
                        <select className="form-select" id="assigned-project-select">
                          <option value="">Select a project to assign</option>
                          {Array.isArray(projects) && projects.map((project) => (
                            <option key={project._id || project.id} value={project._id || project.id}>
                              {project.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => {
                            const select = document.getElementById('assigned-project-select');
                            if (!select || !select.value) return;
                            const project = projects.find((item) => (item._id || item.id) === select.value);
                            if (!project) return;
                            setFormData((prev) => ({
                              ...prev,
                              assignedProjects: [
                                ...prev.assignedProjects,
                                { project: project._id || project.id, projectName: project.name }
                              ]
                            }));
                            select.value = '';
                          }}
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                      <div className="array-items">
                        {formData.assignedProjects.map((project, index) => (
                          <div key={index} className="array-item">
                            <span>{project.projectName || project.name}</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => {
                                setFormData((prev) => ({
                                  ...prev,
                                  assignedProjects: prev.assignedProjects.filter((_, i) => i !== index)
                                }));
                              }}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Project Roles</label>
                    <div className="array-input-group">
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Add a project role"
                          value={projectRoleInput}
                          onChange={(e) => setProjectRoleInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleArrayAdd('projectRoles', projectRoleInput);
                              setProjectRoleInput('');
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => {
                            handleArrayAdd('projectRoles', projectRoleInput);
                            setProjectRoleInput('');
                          }}
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                      <div className="array-items">
                        {formData.projectRoles.map((role, index) => (
                          <div key={index} className="array-item">
                            <span>{role}</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleArrayRemove('projectRoles', index)}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Permissions & Access Tab */}
              {activeTab === 'permissions' && (
                <div className="team-form-section">
                  <h4 className="mb-4">Permissions & Access</h4>
                  
                  <div className="row">
                    <div className="col-md-6">
                      <h5 className="mb-3">Project Permissions</h5>
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.permissions.canCreateProjects}
                          onChange={(e) => handleNestedInputChange('permissions', 'canCreateProjects', e.target.checked)}
                        />
                        <label className="form-check-label">
                          Can Create Projects
                        </label>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.permissions.canEditProjects}
                          onChange={(e) => handleNestedInputChange('permissions', 'canEditProjects', e.target.checked)}
                        />
                        <label className="form-check-label">
                          Can Edit Projects
                        </label>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.permissions.canDeleteProjects}
                          onChange={(e) => handleNestedInputChange('permissions', 'canDeleteProjects', e.target.checked)}
                        />
                        <label className="form-check-label">
                          Can Delete Projects
                        </label>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.permissions.canAssignTasks}
                          onChange={(e) => handleNestedInputChange('permissions', 'canAssignTasks', e.target.checked)}
                        />
                        <label className="form-check-label">
                          Can Assign Tasks
                        </label>
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <h5 className="mb-3">System Permissions</h5>
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.permissions.canViewReports}
                          onChange={(e) => handleNestedInputChange('permissions', 'canViewReports', e.target.checked)}
                        />
                        <label className="form-check-label">
                          Can View Reports
                        </label>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.permissions.canManageTeam}
                          onChange={(e) => handleNestedInputChange('permissions', 'canManageTeam', e.target.checked)}
                        />
                        <label className="form-check-label">
                          Can Manage Team
                        </label>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.permissions.canAccessFinance}
                          onChange={(e) => handleNestedInputChange('permissions', 'canAccessFinance', e.target.checked)}
                        />
                        <label className="form-check-label">
                          Can Access Finance
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Collaboration Settings Tab */}
              {activeTab === 'collaboration' && (
                <div className="team-form-section">
                  <h4 className="mb-4">Collaboration Settings</h4>
                  
                  <div className="row">
                    <div className="col-md-6">
                      <h5 className="mb-3">Notification Preferences</h5>
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.notificationPreferences.emailNotifications}
                          onChange={(e) => handleNestedInputChange('notificationPreferences', 'emailNotifications', e.target.checked)}
                        />
                        <label className="form-check-label">
                          Email Notifications
                        </label>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.notificationPreferences.projectUpdates}
                          onChange={(e) => handleNestedInputChange('notificationPreferences', 'projectUpdates', e.target.checked)}
                        />
                        <label className="form-check-label">
                          Project Updates
                        </label>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.notificationPreferences.taskAssignments}
                          onChange={(e) => handleNestedInputChange('notificationPreferences', 'taskAssignments', e.target.checked)}
                        />
                        <label className="form-check-label">
                          Task Assignments
                        </label>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.notificationPreferences.deadlineReminders}
                          onChange={(e) => handleNestedInputChange('notificationPreferences', 'deadlineReminders', e.target.checked)}
                        />
                        <label className="form-check-label">
                          Deadline Reminders
                        </label>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.notificationPreferences.teamMessages}
                          onChange={(e) => handleNestedInputChange('notificationPreferences', 'teamMessages', e.target.checked)}
                        />
                        <label className="form-check-label">
                          Team Messages
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Information Tab */}
              {activeTab === 'additional' && (
                <div className="team-form-section">
                  <h4 className="mb-4">Additional Information</h4>
                  
                  <div className="form-group mb-4">
                    <label className="form-label">Bio</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={formData.bio}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      placeholder="Tell us about this team member..."
                    />
                  </div>
                  
                  <div className="form-group mb-4">
                    <label className="form-label">Profile Picture</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={(e) => handleInputChange('avatar', e.target.files[0])}
                    />
                  </div>
                  
                  <h5 className="mb-3">Emergency Contact</h5>
                  <div className="form-grid-2">
                    <div className="form-group">
                      <label className="form-label">Contact Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.emergencyContact.name}
                        onChange={(e) => handleNestedInputChange('emergencyContact', 'name', e.target.value)}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Phone Number</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={formData.emergencyContact.phone}
                        onChange={(e) => handleNestedInputChange('emergencyContact', 'phone', e.target.value)}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Relationship</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.emergencyContact.relationship}
                        onChange={(e) => handleNestedInputChange('emergencyContact', 'relationship', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="project-form-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Updating Member...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Update Team Member
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditTeam;